import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {selectSearchRequest} from 'src/components/criteria-strip/services/criteria-strip-slice';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    AccountSearchResultController,
    AccountSearchResultView,
    accountSearchResultStateActions,
} from 'src/modules/account-search-result';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button from '@pb/kit-component-button';
import {Item} from '@pb/kit-component-group';
import {StickyElement} from '@pb/kit-component-sticky-element';


import {AccountsSearchToolsRibbon} from './components/accounts-search-tools-ribbon';
import {AccountSearchResultHeader} from './components/header';

type Props = {
    searchId: string,
};

const PLACE = 'Account Search Result';

export const AccountSearchResult = ({searchId}: Props) => {
    const dispatch = useDispatch();
    const userSearchCriteria = useSelector(selectSearchRequest);
    const navigate = useNavigate();
    const {searchCriteriaOpen} = useEventTracking();
    const searchRequest = useSelector(selectSearchRequest);

    useEffect(() => {
        if (searchRequest) {
            dispatch(accountSearchResultStateActions.setSearchCriteria(searchRequest));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (current.searchRequest && !isDeepEqual({prev, current})) {
            dispatch(accountSearchResultStateActions.setSearchCriteria(current.searchRequest));
        }
    }, {searchRequest});

    const onOpenCriteria = useCallback(() => {
        searchCriteriaOpen({place: 'Modify Criteria Button'});
        navigate(`/search-criteria/${searchId}/users`);
    }, [searchId, navigate, searchCriteriaOpen]);

    const noCriteriaView = useMemo(() => {
        return (
            <ErrorPage
                title="The empty search criteria is not allowed"
                subtitle="Please specify search criteria"
                secondaryButton={(
                    <Button variant="Type.SECONDARY" onClick={onOpenCriteria}>
                        Modify Criteria
                    </Button>
                )}
            />
        );
    }, [onOpenCriteria]);

    if (!userSearchCriteria) {
        return null;
    }

    return (
        <AccountSearchResultController place={PLACE} noCriteriaView={noCriteriaView}>
            {props => (
                <Item grow={1}>
                    <AccountSearchResultHeader searchId={searchId} totalCount={props.totalCount}/>
                    <StickyElement className="sticky-tool-ribbon">
                        <AccountsSearchToolsRibbon
                            place={PLACE}
                            adminId={props.adminId}
                            isLoading={props.isLoading}
                            getAllEntityIds={props.getAllEntityIds}
                            templates={props.templates}
                            activeSearchTemplate={props.activeSearchTemplate}
                            setActiveSearchTemplate={props.setActiveSearchTemplate}
                            loadTemplates={props.loadTemplates}
                            loadActiveSearchTemplate={props.loadActiveSearchTemplate}
                            refreshResults={props.refreshResults}
                        />
                    </StickyElement>
                    <AccountSearchResultView
                        getAllEntityIds={props.getAllEntityIds}
                        place={PLACE}
                        activeSearchTemplate={props.activeSearchTemplate}
                    />
                </Item>
            )}
        </AccountSearchResultController>
    );
};
